<template>
    <aside class="flex w-[480px] flex-col border-l border-slate-200 bg-white">
        <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div class="min-w-0">
                <h2 class="text-sm font-semibold text-slate-800">Skills Inspector</h2>
                <div class="text-[11px] text-slate-400">{{ filteredSkills.length }} of {{ skills.length }} skills</div>
            </div>
            <div class="flex items-center gap-2">
                <label for="skills-filter-available" class="flex items-center gap-1 text-[11px] text-slate-500">
                    <input id="skills-filter-available" v-model="showOnlyAvailable" type="checkbox" class="rounded border-slate-300 text-slate-700 focus:ring-slate-500" />
                    Available only
                </label>
                <button
                    type="button"
                    class="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="skills-refresh"
                    :disabled="loading"
                    @click="load">
                    {{ loading ? "Loading..." : "Refresh" }}
                </button>
                <button
                    type="button"
                    class="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                    data-testid="skills-close"
                    aria-label="Close skills inspector"
                    @click="emit('close')">
                    ✕
                </button>
            </div>
        </div>

        <div class="flex-1 overflow-y-auto px-4 py-3 text-xs">
            <div v-if="loading && skills.length === 0" class="text-slate-500">Loading skills...</div>
            <div v-else-if="error" class="text-red-600">Failed to load skills.</div>
            <div v-else-if="skills.length === 0" class="text-slate-500">No skills registered.</div>

            <div v-else class="space-y-2">
                <div v-for="skill in filteredSkills" :key="skill.name" class="rounded border border-slate-200" :class="{ 'opacity-50': !skill.available }">
                    <button
                        type="button"
                        class="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
                        :data-testid="`skill-header-${skill.name}`"
                        @click="toggle(skill.name)">
                        <span class="mt-0.5 text-slate-400">{{ expanded.has(skill.name) ? "▾" : "▸" }}</span>
                        <div class="min-w-0 flex-1">
                            <div class="flex items-center gap-2">
                                <span class="font-semibold text-slate-800">{{ skill.name }}</span>
                                <span v-if="skill.has_availability_gate" class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"> gated </span>
                                <span v-if="skill.available" class="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700"> available </span>
                                <span v-else class="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"> unavailable </span>
                            </div>
                            <div class="mt-0.5 text-slate-500">{{ skill.description }}</div>
                        </div>
                    </button>

                    <div v-if="expanded.has(skill.name)" class="border-t border-slate-200 px-3 py-2">
                        <div class="mb-2 flex flex-wrap gap-2">
                            <span class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600"> v{{ skill.version_hash }} </span>
                            <span v-for="tool in skill.allowed_tools" :key="tool" class="rounded bg-purple-50 px-1.5 py-0.5 font-mono text-[10px] text-purple-700">
                                {{ tool }}
                            </span>
                        </div>

                        <div v-if="skill.files.length > 0" class="mb-2">
                            <div class="mb-1 text-[10px] font-medium uppercase text-slate-400">Supporting files</div>
                            <div class="flex flex-wrap gap-1">
                                <span v-for="file in skill.files" :key="file" class="rounded bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                                    {{ file }}
                                </span>
                            </div>
                        </div>

                        <div class="mt-2">
                            <div class="mb-1 text-[10px] font-medium uppercase text-slate-400">Full prompt</div>
                            <pre class="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 font-mono text-[11px] leading-relaxed text-slate-700">{{
                                skill.body
                            }}</pre>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </aside>
</template>

<script setup lang="ts">
import { fetchApi } from "~vue/api";
import { logError } from "~vue/utils/logError";
import { computed, onMounted, ref } from "vue";

import type { SkillInfo, SkillsListResponse } from "./types";

const emit = defineEmits<{
    close: [];
}>();

const skills = ref<SkillInfo[]>([]);
const loading = ref(false);
const error = ref(false);
const expanded = ref<Set<string>>(new Set());
const showOnlyAvailable = ref(false);

const filteredSkills = computed(() => {
    if (!showOnlyAvailable.value) {
        return skills.value;
    }
    return skills.value.filter((s) => s.available);
});

const toggle = (name: string) => {
    if (expanded.value.has(name)) {
        expanded.value.delete(name);
    } else {
        expanded.value.add(name);
    }
    // Force reactivity on Set mutation
    expanded.value = new Set(expanded.value);
};

const load = async () => {
    loading.value = true;
    error.value = false;
    try {
        const response = await fetchApi("/v1/nadia2/skills");
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const payload: SkillsListResponse = await response.json();
        skills.value = payload.skills;
    } catch (err) {
        error.value = true;
        logError(err instanceof Error ? err : new Error(String(err)));
    } finally {
        loading.value = false;
    }
};

onMounted(load);
</script>
